import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  ArrowLeft,
  Building2,
  Upload,
  Loader2,
  Save,
  X,
  Image as ImageIcon,
  KeyRound,
  AlertTriangle,
  Download,
  Printer,
  Eye,
  EyeOff,
  ShieldAlert,
  Globe,
  Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Layout } from "../components/Layout";
import { getCurrentEntity, updateEntity, uploadImage, requestKeyRegeneration, cancelKeyRegeneration, getNewKey, confirmKeyViewed, changePassword } from "../lib/api";
import { useAppConfig } from "../lib/AppConfigContext";
import { toast } from "sonner";
import { languages } from "../i18n";
import { PasswordStrength } from "../components/PasswordStrength";
import { API_URL } from "../lib/config";

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [entity, setEntity] = useState(null);
  const [name, setName] = useState("");
  const [formData, setFormData] = useState({
    cif: "", address: "", city: "", postal_code: "", province: "",
    phone: "", website: "", contact_person: "", contact_phone: "", language: "ca",
    notification_email: ""
  });
  const [keyRegenLoading, setKeyRegenLoading] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const { appName } = useAppConfig();
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Change password state
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showKeyConfirmDialog, setShowKeyConfirmDialog] = useState(false);
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [showRegenFinalDialog, setShowRegenFinalDialog] = useState(false);
  const [showCancelRegenDialog, setShowCancelRegenDialog] = useState(false);

  useEffect(() => {
    loadEntity();
  }, []);

  const loadEntity = async () => {
    try {
      const response = await getCurrentEntity();
      setEntity(response.data);
      setName(response.data.name);
      setLogoUrl(response.data.logo_url);
      setFormData({
        cif: response.data.cif || "",
        address: response.data.address || "",
        city: response.data.city || "",
        postal_code: response.data.postal_code || "",
        province: response.data.province || "",
        phone: response.data.phone || "",
        website: response.data.website || "",
        contact_person: response.data.contact_person || "",
        contact_phone: response.data.contact_phone || "",
        language: response.data.language || "ca",
        notification_email: response.data.notification_email || "",
      });
    } catch (error) {
      toast.error(t('errors.generic'));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('consulta.proposals.proposalImage').split('(')[1]?.split(')')[0] || "JPG o PNG");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.maxSize5MB'));
      return;
    }
    
    setUploading(true);
    try {
      const response = await uploadImage(file);
      setLogoUrl(response.data.url);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateEntity({
        name: name.trim(),
        logo_url: logoUrl,
        ...formData
      });
      
      // Update localStorage
      localStorage.setItem("entity_name", response.data.name);
      if (response.data.logo_url) {
        localStorage.setItem("entity_logo", response.data.logo_url);
      } else {
        localStorage.removeItem("entity_logo");
      }
      if (response.data.language) {
        localStorage.setItem("entity_language", response.data.language);
        i18n.changeLanguage(response.data.language);
      }
      
      toast.success(t('consulta.messages.updated'));
      
      // Reload to update header
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = () => {
    setLogoUrl(null);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const pw = pwForm.newPw;
    const hasMinLen = pw.length >= 10;
    const hasUpper = /[A-Z]/.test(pw);
    const hasLower = /[a-z]/.test(pw);
    const hasNum = /[0-9]/.test(pw);
    const hasSpecial = /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(pw);
    if (!hasMinLen || !hasUpper || !hasLower || !hasNum || !hasSpecial) {
      toast.error(t('passwordPolicy.weak'));
      return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      toast.error(t('settings.passwordsDontMatch'));
      return;
    }
    setPwLoading(true);
    try {
      await changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success(t('settings.passwordChanged'));
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (err) {
      if (err.response?.data?.detail === "wrong_current_password") {
        toast.error(t('settings.wrongCurrentPassword'));
      } else if (err.response?.data?.detail === "password_too_short") {
        toast.error(t('settings.passwordTooShort'));
      } else {
        toast.error(t('errors.generic'));
      }
    } finally {
      setPwLoading(false);
    }
  };

  const handleRequestKeyRegen = () => {
    setShowRegenDialog(true);
  };

  const handleRegenStep1Confirmed = () => {
    setShowRegenDialog(false);
    setShowRegenFinalDialog(true);
  };

  const handleRegenFinalConfirmed = async () => {
    setShowRegenFinalDialog(false);
    setKeyRegenLoading(true);
    try {
      await requestKeyRegeneration();
      toast.success(t('settings.regenRequestSent'));
      const res = await getCurrentEntity();
      setEntity(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.sendRequest'));
    } finally {
      setKeyRegenLoading(false);
    }
  };

  const handleCancelKeyRegen = () => {
    setShowCancelRegenDialog(true);
  };

  const handleCancelRegenConfirmed = async () => {
    setShowCancelRegenDialog(false);
    try {
      await cancelKeyRegeneration();
      toast.success(t('settings.requestCancelled'));
      const res = await getCurrentEntity();
      setEntity(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.cancelling'));
    }
  };

  const handleViewNewKey = async () => {
    try {
      const res = await getNewKey();
      setNewKey(res.data);
      setShowNewKey(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.gettingKey'));
    }
  };

  const handleDownloadKey = () => {
    if (!newKey) return;
    const blob = new Blob([newKey.private_key], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(appName || "clau").replace(/\s+/g, "_")}_${t("register.privateKeyFilename")}_${(newKey.entity_name || "").replace(/\s+/g, "_")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t('settings.keyDownloaded'));
  };

  const handlePrintKey = () => {
    if (!newKey) return;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${t('settings.privateKeyWindow')}</title><style>body{font-family:monospace;padding:40px;} h2{margin-bottom:20px;}</style></head><body><h2>${t('settings.privateKeyWindow')} - ${newKey.entity_name}</h2><pre style="white-space:pre-wrap;word-break:break-all;border:1px solid #ccc;padding:20px;">${newKey.private_key}</pre><p style="margin-top:20px;color:red;font-weight:bold;">${t('settings.privateKeySaveWarning')}</p></body></html>`);
    w.document.close();
    w.print();
  };

  const handleConfirmKeyViewed = () => {
    setShowKeyConfirmDialog(true);
  };

  const handleConfirmKeyViewedAccept = async () => {
    setShowKeyConfirmDialog(false);
    try {
      await confirmKeyViewed();
      toast.success(t('settings.keyConfirmedDeleted'));
      setNewKey(null);
      setShowNewKey(false);
      const res = await getCurrentEntity();
      setEntity(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.confirming'));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="content-container max-w-2xl" data-testid="settings-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          <h1 className="text-3xl font-heading font-bold text-slate-900">
            {t('settings.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('settings.subtitle')}
          </p>
        </motion.div>

        {/* Save button - fixed top right */}
        <div className="flex justify-end -mt-14 mb-6">
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="bg-green-600 hover:bg-green-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('common.save')}
          </Button>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-lg p-8"
        >
          {/* Logo Section */}
          <div className="mb-8">
            <Label className="text-base font-medium text-slate-900 mb-4 block">
              {t('settings.logo')}
            </Label>
            <p className="text-sm text-slate-500 mb-4">
              {t('settings.logoDesc')}
            </p>
            
            <div className="flex items-start gap-6">
              {/* Preview */}
              <div className="flex-shrink-0">
                {logoUrl ? (
                  <div className="relative">
                    <img 
                      src={`${API_URL}${logoUrl}`}
                      alt="Logo"
                      className="w-32 h-32 object-contain rounded-lg border border-slate-200 bg-white"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      data-testid="remove-logo-btn"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-32 h-32 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                    <Building2 className="h-12 w-12 text-slate-300" />
                  </div>
                )}
              </div>
              
              {/* Upload button */}
              <div>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  ref={fileInputRef}
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  className="hidden"
                  data-testid="logo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-logo-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {logoUrl ? t('settings.changeImage') : t('settings.uploadImage')}
                </Button>
                <p className="text-xs text-slate-400 mt-2">
                  {t('settings.recommended')}
                </p>
              </div>
            </div>
          </div>

          {/* Name Section */}
          <div className="mb-8">
            <Label htmlFor="name" className="text-base font-medium text-slate-900 mb-2 block">
              {t('settings.entityName')}
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.entityNamePlaceholder')}
              className="max-w-md"
              data-testid="entity-name-input"
            />
          </div>

          {/* Email (readonly) */}
          <div className="mb-4">
            <Label className="text-base font-medium text-slate-900 mb-2 block">
              {t('settings.email')}
            </Label>
            <p className="text-slate-600">{entity?.email}</p>
            <p className="text-xs text-slate-400 mt-1">
              {t('settings.emailReadonly')}
            </p>
          </div>

          {/* Notification Email */}
          <div className="mb-8">
            <Label htmlFor="notification_email" className="text-base font-medium text-slate-900 mb-2 block">
              {t('settings.notificationEmail')}
            </Label>
            <Input
              id="notification_email"
              type="email"
              value={formData.notification_email}
              onChange={(e) => setFormData({...formData, notification_email: e.target.value})}
              placeholder={t('settings.notificationEmailPlaceholder')}
              className="max-w-md"
              data-testid="entity-notification-email-input"
            />
          </div>

          {/* Change Password */}
          <div className="mb-8 border-t border-slate-200 pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="h-4 w-4 text-slate-700" />
              <Label className="text-base font-medium text-slate-900">
                {t('settings.changePasswordTitle')}
              </Label>
            </div>
            <p className="text-sm text-slate-500 mb-4">{t('settings.changePasswordDesc')}</p>
            <form onSubmit={handleChangePassword} className="space-y-3 max-w-md">
              <div className="relative">
                <Input
                  type={showCurrentPw ? "text" : "password"}
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  placeholder={t('settings.currentPasswordPlaceholder')}
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showNewPw ? "text" : "password"}
                  value={pwForm.newPw}
                  onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
                  placeholder={t('settings.newPasswordPlaceholder')}
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordStrength password={pwForm.newPw} minLength={10} />
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                placeholder={t('settings.confirmPasswordPlaceholder')}
                data-testid="confirm-password-input"
              />
              <Button
                type="submit"
                disabled={pwLoading || !pwForm.current || !pwForm.newPw || !pwForm.confirm}
                variant="outline"
                className="border-slate-300"
                data-testid="change-password-btn"
              >
                {pwLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                {t('settings.changePasswordBtn')}
              </Button>
            </form>
          </div>

          {/* Language */}
          <div className="mb-8">
            <Label className="text-base font-medium text-slate-900 mb-2 block">
              <Globe className="h-4 w-4 inline mr-2" />
              {t('settings.languageLabel')}
            </Label>
            <p className="text-sm text-slate-500 mb-3">
              {t('settings.languageDesc')}
            </p>
            <div className="flex gap-2 flex-wrap">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setFormData({...formData, language: lang.code})}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    formData.language === lang.code
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          {/* CIF */}
          <div className="mb-6">
            <Label htmlFor="cif" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.cif')}</Label>
            <Input id="cif" value={formData.cif} onChange={(e) => setFormData({...formData, cif: e.target.value})} placeholder={t('settings.cifPlaceholder')} className="max-w-xs" data-testid="entity-cif-input" />
          </div>

          {/* Address */}
          <div className="mb-6">
            <Label htmlFor="address" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.addressLabel')}</Label>
            <Input id="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder={t('settings.addressPlaceholder')} className="max-w-md" data-testid="entity-address-input" />
          </div>

          {/* City, Postal Code, Province */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <Label htmlFor="city" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.cityLabel')}</Label>
              <Input id="city" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder={t('settings.cityPlaceholder')} data-testid="entity-city-input" />
            </div>
            <div>
              <Label htmlFor="postal_code" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.postalCodeLabel')}</Label>
              <Input id="postal_code" value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} placeholder={t('settings.postalCodePlaceholder')} data-testid="entity-postal-code-input" />
            </div>
            <div>
              <Label htmlFor="province" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.provinceLabel')}</Label>
              <Input id="province" value={formData.province} onChange={(e) => setFormData({...formData, province: e.target.value})} placeholder={t('settings.provincePlaceholder')} data-testid="entity-province-input" />
            </div>
          </div>

          {/* Phone, Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.phoneLabel')}</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder={t('settings.phonePlaceholder')} data-testid="entity-phone-input" />
            </div>
            <div>
              <Label htmlFor="website" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.webLabel')}</Label>
              <Input id="website" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder={t('settings.websitePlaceholder')} data-testid="entity-website-input" />
            </div>
          </div>

          {/* Contact person */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div>
              <Label htmlFor="contact_person" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.contactPersonLabel')}</Label>
              <Input id="contact_person" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} placeholder={t('settings.contactPersonPlaceholder')} data-testid="entity-contact-person-input" />
            </div>
            <div>
              <Label htmlFor="contact_phone" className="text-sm font-medium text-slate-900 mb-1 block">{t('settings.contactPhoneLabel')}</Label>
              <Input id="contact_phone" value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} placeholder={t('settings.contactPhonePlaceholder')} data-testid="entity-contact-phone-input" />
            </div>
          </div>
        </motion.div>

        {/* Key Regeneration Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-lg p-8 mt-6"
          data-testid="key-regen-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              {t('settings.privateKeyTitle')}
            </h2>
          </div>

          {/* No request */}
          {!entity?.key_regen_status && !showNewKey && (
            <div data-testid="key-regen-no-request">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">{t('settings.lostPrivateKey')}</p>
                    <p>{t('settings.keyRegenExplanation')} <strong>{t('settings.keyRegenWarning')}</strong></p>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleRequestKeyRegen}
                disabled={keyRegenLoading}
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="request-key-regen-btn"
              >
                {keyRegenLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ShieldAlert className="h-4 w-4 mr-2" />
                )}
                {t('settings.requestRegen')}
              </Button>
            </div>
          )}

          {/* Pending */}
          {entity?.key_regen_status === "pending" && (
            <div data-testid="key-regen-pending">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <Loader2 className="h-5 w-5 text-orange-600 flex-shrink-0 animate-spin" />
                  <div className="text-sm text-orange-800">
                    <p className="font-medium mb-1">{t('settings.pendingApprovalTitle')}</p>
                    <p>{t('settings.pendingApprovalDesc')}</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleCancelKeyRegen}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="cancel-key-regen-btn"
              >
                <X className="h-4 w-4 mr-2" />
                {t('settings.cancelRequest')}
              </Button>
            </div>
          )}

          {/* Approved - key available */}
          {entity?.key_regen_status === "approved" && entity?.new_key_available && !showNewKey && (
            <div data-testid="key-regen-approved">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <KeyRound className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-medium mb-1">{t('settings.newKeyAvailable')}</p>
                    <p>{t('settings.approvedRegenDesc')}</p>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleViewNewKey}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="view-new-key-btn"
              >
                <Eye className="h-4 w-4 mr-2" />
                {t('settings.viewNewKey')}
              </Button>
            </div>
          )}

          {/* Viewing the new key */}
          {showNewKey && newKey && (
            <div data-testid="key-regen-view-key">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">{t('settings.saveKeyImmediately')}</p>
                    <p>{t('settings.saveKeyDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-emerald-400 max-h-48 overflow-y-auto whitespace-pre-wrap break-all mb-4" data-testid="new-key-display">
                {newKey.private_key}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  onClick={handleDownloadKey}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="download-new-key-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('settings.download')}
                </Button>
                <Button
                  onClick={handlePrintKey}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="print-new-key-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  {t('settings.print')}
                </Button>
              </div>

              <Button
                onClick={handleConfirmKeyViewed}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-key-viewed-btn"
              >
                {t('settings.confirmKeySaved')}
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Simple confirmation dialogs - no Radix dependency */}
      {showKeyConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="confirm-key-viewed-dialog">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowKeyConfirmDialog(false)} />
          <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">{t('settings.confirmKeySaved')}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">{t('settings.confirmKeyViewed')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowKeyConfirmDialog(false)} data-testid="confirm-key-viewed-cancel">{t("common.cancel")}</Button>
              <Button onClick={handleConfirmKeyViewedAccept} data-testid="confirm-key-viewed-accept">{t("common.confirm")}</Button>
            </div>
          </div>
        </div>
      )}

      {showRegenDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="regen-warning-dialog">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowRegenDialog(false)} />
          <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">{t('settings.regenTitle')}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">{t('settings.regenWarning')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRegenDialog(false)}>{t("common.cancel")}</Button>
              <Button onClick={handleRegenStep1Confirmed}>{t("common.confirm")}</Button>
            </div>
          </div>
        </div>
      )}

      {showRegenFinalDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="regen-final-dialog">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowRegenFinalDialog(false)} />
          <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">{t('settings.regenTitle')}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">{t('settings.lastConfirmRegen')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRegenFinalDialog(false)}>{t("common.cancel")}</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleRegenFinalConfirmed}>{t("common.confirm")}</Button>
            </div>
          </div>
        </div>
      )}

      {showCancelRegenDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="cancel-regen-dialog">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowCancelRegenDialog(false)} />
          <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-lg p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2">{t('common.confirm')}</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line mb-6">{t('settings.cancelRegenConfirm')}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancelRegenDialog(false)}>{t("common.cancel")}</Button>
              <Button onClick={handleCancelRegenConfirmed}>{t("common.confirm")}</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
